import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Loader2, Trash2, Upload, LogOut, BookOpen, Plus, Pencil, X, Eye, EyeOff, ExternalLink, Calendar, DollarSign, FileText, Star, Gift, Send, Check, ChevronDown, ChevronRight, Heart, BarChart3 } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import BookCover from '../components/BookCover';
import FlipbookViewer from '../components/FlipbookViewer';
import { loginWithGoogle } from '../utils/auth';

import { BACKEND_URL, API } from '../utils/api';
import { authFetch, clearToken } from '../utils/session';
const R2_URL = process.env.REACT_APP_R2_URL || BACKEND_URL + "/api/static";
const CHUNK_SIZE = 1 * 1024 * 1024; // 1MB chunks - maximum proxy compatibility
const MAX_RETRIES = 3;

const uploadChunkWithRetry = async (url, buildFormData, retries = MAX_RETRIES) => {
  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      const fd = buildFormData();
      const r = await authFetch(url, { method: 'POST', body: fd });
      if (r.ok) return r;
      if (attempt === retries) {
        let msg = `HTTP ${r.status}`;
        try { const d = await r.json(); msg = d.detail || msg; } catch(e) {
          try { const t = await r.text(); msg = `HTTP ${r.status}: ${t.substring(0, 150)}`; } catch(e2) {}
        }
        throw new Error(msg);
      }
    } catch (err) {
      if (attempt === retries) throw err;
    }
    await new Promise(r => setTimeout(r, 1500 * attempt));
  }
};

const AdminPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(location.state?.user || null);
  const [loading, setLoading] = useState(!location.state?.user);
  const [books, setBooks] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingBook, setEditingBook] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [editCharFocused, setEditCharFocused] = useState(false);
  const [orders, setOrders] = useState([]);
  const [gifts, setGifts] = useState([]);
  const [clients, setClients] = useState([]);
  const [stats, setStats] = useState(null);
  const [previewBook, setPreviewBook] = useState(null);
  const [expandedUserId, setExpandedUserId] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Broadcast email state
  const [broadcastBook, setBroadcastBook] = useState(null);
  const [broadcastRecipients, setBroadcastRecipients] = useState([]);
  const [broadcastSelected, setBroadcastSelected] = useState([]);
  const [broadcastSubject, setBroadcastSubject] = useState('');
  const [broadcastHeadline, setBroadcastHeadline] = useState('');
  const [broadcastBody, setBroadcastBody] = useState('');
  const [broadcastCta, setBroadcastCta] = useState('');
  const [broadcastSending, setBroadcastSending] = useState(false);
  const [broadcastLoading, setBroadcastLoading] = useState(false);

  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [category, setCategory] = useState('Nuevo Testamento');
  const [characters, setCharacters] = useState([]);
  const [charInput, setCharInput] = useState('');
  const [allCharacters, setAllCharacters] = useState([]);
  const [charFocused, setCharFocused] = useState(false);
  const [pdfFile, setPdfFile] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [previewData, setPreviewData] = useState(null);

  // Auth check
  useEffect(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (window.location.hash?.includes('session_id=')) { setLoading(false); return; }
    if (location.state?.user) {
      if (location.state.user.role !== 'admin') { navigate('/'); return; }
      setLoading(false); return;
    }
    authFetch(`${API}/auth/me`, {  })
      .then((r) => { if (!r.ok) throw new Error(); return r.json(); })
      .then((u) => {
        if (u.role !== 'admin') { navigate('/'); return; }
        setUser(u); setLoading(false);
      })
      .catch(() => { setUser(null); setLoading(false); });
  }, [location.state]);

  const fetchBooks = useCallback(async () => {
    if (!user) return;
    try {
      const r = await authFetch(`${API}/admin/books`, {  });
      if (r.ok) setBooks(await r.json());
    } catch (e) { console.error(e); }
  }, [user]);

  useEffect(() => { fetchBooks(); }, [fetchBooks]);

  const fetchReviews = useCallback(async () => {
    if (!user) return;
    try {
      const r = await authFetch(`${API}/admin/reviews`, {  });
      if (r.ok) setReviews(await r.json());
    } catch (e) { /* ignore */ }
  }, [user]);
  useEffect(() => { fetchReviews(); }, [fetchReviews]);

  const fetchOrders = useCallback(async () => {
    if (!user) return;
    try { const r = await authFetch(`${API}/admin/orders`, {}); if (r.ok) setOrders(await r.json()); } catch (e) { console.error('Fetch orders failed:', e); }
  }, [user]);
  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const fetchGifts = useCallback(async () => {
    if (!user) return;
    try { const r = await authFetch(`${API}/admin/gifts`, {}); if (r.ok) setGifts(await r.json()); } catch (e) { console.error('Fetch gifts failed:', e); }
  }, [user]);
  useEffect(() => { fetchGifts(); }, [fetchGifts]);

  const fetchClients = useCallback(async () => {
    if (!user) return;
    try { const r = await authFetch(`${API}/admin/users`, {  }); if (r.ok) setClients(await r.json()); } catch (e) { console.error('Fetch clients failed:', e); }
  }, [user]);
  useEffect(() => { fetchClients(); }, [fetchClients]);

  const fetchStats = useCallback(async () => {
    if (!user) return;
    try { const r = await authFetch(`${API}/admin/stats`, {  }); if (r.ok) setStats(await r.json()); } catch (e) { console.error('Fetch stats failed:', e); }
  }, [user]);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  const handleDeleteReview = async (reviewId) => {
    await authFetch(`${API}/admin/reviews/${reviewId}`, { method: 'DELETE' });
    fetchReviews();
  };

  const toggleUserDetail = async (userId) => {
    if (expandedUserId === userId) {
      setExpandedUserId(null);
      setUserDetail(null);
      return;
    }
    setExpandedUserId(userId);
    setLoadingDetail(true);
    setUserDetail(null);
    try {
      const r = await authFetch(`${API}/admin/users/${userId}/detail`);
      if (r.ok) setUserDetail(await r.json());
    } catch (e) { console.error('Fetch user detail failed:', e); }
    setLoadingDetail(false);
  };

  // Fetch all existing characters for autocomplete
  const fetchCharacters = useCallback(async () => {
    try {
      const r = await fetch(`${API}/characters`);
      if (r.ok) setAllCharacters(await r.json());
    } catch (e) { /* ignore */ }
  }, []);
  useEffect(() => { fetchCharacters(); }, [fetchCharacters]);

  const addCharacter = (name) => {
    const trimmed = name.trim();
    if (trimmed && !characters.includes(trimmed)) {
      setCharacters([...characters, trimmed]);
    }
    setCharInput('');
  };

  const removeCharacter = (name) => {
    setCharacters(characters.filter(c => c !== name));
  };

  // Biblical character suggestions based on title keywords
  const biblicalSuggestions = (() => {
    const t = title.toLowerCase();
    const map = {
      'jesús': ['Jesús', 'Pedro', 'Juan', 'Santiago', 'Mateo', 'María', 'María Magdalena', 'Judas', 'Tomás', 'Andrés', 'Bartimeo', 'Lázaro', 'Marta', 'Zaqueo', 'Nicodemo', 'Pilato'],
      'david': ['David', 'Goliat', 'Saúl', 'Jonatán', 'Samuel', 'Betsabé'],
      'moisés': ['Moisés', 'Aarón', 'Faraón', 'Miriam', 'Séfora', 'Josué'],
      'noé': ['Noé', 'Sem', 'Cam', 'Jafet'],
      'daniel': ['Daniel', 'Nabucodonosor', 'Darío'],
      'abraham': ['Abraham', 'Sara', 'Isaac', 'Agar', 'Ismael'],
      'josé': ['José', 'Jacob', 'Benjamín', 'Potifar'],
      'jonás': ['Jonás'],
      'ruth': ['Ruth', 'Noemí', 'Booz'],
      'esther': ['Esther', 'Mardoqueo', 'Amán', 'Asuero'],
      'sansón': ['Sansón', 'Dalila'],
      'elías': ['Elías', 'Eliseo', 'Jezabel', 'Acab'],
      'salomón': ['Salomón'],
      'adán': ['Adán', 'Eva'],
      'creación': ['Adán', 'Eva', 'Dios'],
      'babel': ['Nimrod'],
      'samaritano': ['Jesús'],
      'oveja': ['Jesús'],
      'hijo pródigo': ['Jesús'],
      'nacimiento': ['Jesús', 'María', 'José', 'Herodes', 'Los Reyes Magos', 'Los Pastores'],
      'resurrec': ['Jesús', 'María Magdalena', 'Pedro', 'Juan'],
      'milagro': ['Jesús', 'Pedro'],
      'parábola': ['Jesús'],
      'bartimeo': ['Jesús', 'Bartimeo'],
      'jairo': ['Jesús', 'Jairo'],
      'lázaro': ['Jesús', 'Lázaro', 'Marta', 'María'],
    };
    const suggestions = new Set();
    Object.entries(map).forEach(([key, names]) => {
      if (t.includes(key)) names.forEach(n => suggestions.add(n));
    });
    // Add from existing DB characters too
    allCharacters.forEach(c => suggestions.add(c.name));
    // Filter out already added
    return [...suggestions].filter(n => !characters.includes(n));
  })();

  const charSuggestions = charInput.length > 0
    ? biblicalSuggestions.filter(n => n.toLowerCase().includes(charInput.toLowerCase()))
    : biblicalSuggestions.slice(0, 8);

  const handleLogin = () => {
    loginWithGoogle('/admin');
  };

  const handleLogout = async () => {
    await authFetch(`${API}/auth/logout`, { method: 'POST' });
    setUser(null);
  };

  const handleAdminGift = async (book) => {
    const message = window.prompt('Mensaje del regalo (opcional):', 'Un regalo de Museo de la Fe');
    if (message === null) return;
    const res = await authFetch(`${API}/admin/gift`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ slugs: [book.slug], message }),
    });
    if (res.ok) {
      const data = await res.json();
      navigator.clipboard.writeText(data.url);
      toast.success(`Link copiado: ${data.url}`);
    } else {
      toast.error('Error al crear regalo');
    }
  };

  // Upload PDF and analyze with AI
  const handlePreview = () => {
    if (!title || !price) { toast.error('Completa título y precio'); return; }
    setPreviewData({ title, description, price: parseFloat(price), coverUrl: coverPreview || null, coverColors: ['#6366F1', '#4F46E5'] });
  };

  const pollBookStatus = async (slug) => {
    const maxAttempts = 120; // 4 minutes max (2s intervals)
    for (let i = 0; i < maxAttempts; i++) {
      await new Promise(r => setTimeout(r, 2000));
      try {
        const r = await authFetch(`${API}/admin/upload/status/${slug}`);
        if (!r.ok) continue;
        const data = await r.json();
        if (data.status === 'published') {
          setUploadProgress(100);
          return true;
        }
        if (data.status === 'error') {
          throw new Error(data.error || 'Error procesando PDF');
        }
        if (data.status === 'uploading') {
          setUploadProgress(95);
        }
      } catch (err) {
        if (err.message?.includes('Error procesando')) throw err;
      }
    }
    throw new Error('Tiempo de espera agotado. El libro puede seguir procesándose en segundo plano.');
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!pdfFile || !title || !price || !coverFile) { toast.error('Completa todos los campos y sube PDF + portada'); return; }
    setUploading(true); setUploadProgress(0); setPreviewData(null);
    try {
      const initRes = await authFetch(`${API}/admin/upload/init`, { method: 'POST' });
      if (!initRes.ok) throw new Error('Error al iniciar subida');
      const { upload_id } = await initRes.json();

      const totalChunks = Math.ceil(pdfFile.size / CHUNK_SIZE);
      for (let i = 0; i < totalChunks; i++) {
        const makeForm = () => {
          const fd = new FormData();
          fd.append('upload_id', upload_id);
          fd.append('chunk_index', i.toString());
          fd.append('chunk', pdfFile.slice(i * CHUNK_SIZE, Math.min((i + 1) * CHUNK_SIZE, pdfFile.size)), 'chunk');
          return fd;
        };
        await uploadChunkWithRetry(`${API}/admin/upload/chunk`, makeForm);
        setUploadProgress(Math.round(((i + 1) / totalChunks) * 70));
      }

      setUploadProgress(75);
      const coverForm = new FormData();
      coverForm.append('upload_id', upload_id);
      coverForm.append('cover', coverFile);
      const coverRes = await authFetch(`${API}/admin/upload/cover`, { method: 'POST', body: coverForm });
      if (!coverRes.ok) { let m='Error portada'; try{const d=await coverRes.json();m=d.detail||m;}catch(e){} throw new Error(m); }

      setUploadProgress(80);
      const completeRes = await authFetch(`${API}/admin/upload/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ upload_id, title, description, price: parseFloat(price), category, characters }),
      });
      if (!completeRes.ok) { let m='Error al procesar'; try{const d=await completeRes.json();m=d.detail||m;}catch(e){} throw new Error(m); }

      const bookData = await completeRes.json();
      setUploadProgress(85);
      toast.info('Procesando páginas del PDF...', { duration: 120000, id: 'processing' });

      // Poll for completion
      await pollBookStatus(bookData.slug);
      toast.dismiss('processing');
      toast.success(`"${title}" publicado exitosamente!`);
      resetForm(); fetchBooks(); fetchCharacters();
    } catch (err) {
      toast.dismiss('processing');
      toast.error(err.message || 'Error al subir');
    }
    finally { setUploading(false); setUploadProgress(0); }
  };

  const resetForm = () => {
    setTitle(''); setDescription(''); setPrice(''); setPdfFile(null);
    setCoverFile(null); setCoverPreview(null); setCategory('Nuevo Testamento');
    setCharacters([]); setCharInput(''); setShowForm(false); setPreviewData(null);
  };

  const handleDelete = async (slug, bookTitle) => {
    if (!window.confirm(`¿Eliminar "${bookTitle}"?`)) return;
    await authFetch(`${API}/admin/books/${slug}`, { method: 'DELETE' });
    toast.success('Libro eliminado');
    fetchBooks();
  };

  const handleToggleVisibility = async (book) => {
    const newHidden = !book.hidden;
    const res = await authFetch(`${API}/admin/books/${book.slug}/visibility`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ hidden: newHidden }),
    });
    if (res.ok) {
      toast.success(newHidden ? `"${book.title}" oculto` : `"${book.title}" visible`);
      fetchBooks();
    } else {
      toast.error('Error al cambiar visibilidad');
    }
  };

  const handleEdit = (book) => {
    setEditingBook({ ...book, newTitle: book.title, newDescription: book.description || '', newPrice: book.price?.toString() || '', newCategory: book.category || 'Nuevo Testamento', newCharacters: book.characters || [], newCharInput: '', newCoverFile: null, newCoverPreview: null });
  };

  const openBroadcast = async (book) => {
    setBroadcastBook(book);
    setBroadcastLoading(true);
    setBroadcastSubject(`${book.title} — Una nueva historia de fe para tus peques`);
    setBroadcastHeadline(`{nombre}, hay una nueva historia esperando por tus peques`);
    setBroadcastBody(`"${book.title}" acaba de llegar al Museo de la Fe.\n\nCada página está llena de ilustraciones que van a hacer brillar los ojitos de tus pequeños mientras descubren juntos una historia que les enseñará valores que llevarán en su corazoncito para siempre.\n\nNo dejes pasar esta oportunidad de crear un momento inolvidable en familia.`);
    setBroadcastCta(`Quiero leerlo con mis peques`);
    try {
      const r = await authFetch(`${API}/admin/books/${book.slug}/recipients`);
      if (r.ok) {
        const data = await r.json();
        setBroadcastRecipients(data.recipients);
        setBroadcastSelected(data.recipients.map(u => u.user_id));
      }
    } catch (e) { console.error('Fetch recipients failed:', e); }
    setBroadcastLoading(false);
  };

  const sendBroadcast = async () => {
    if (!broadcastBook || broadcastSelected.length === 0) return;
    setBroadcastSending(true);
    try {
      const r = await authFetch(`${API}/admin/books/${broadcastBook.slug}/broadcast`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subject: broadcastSubject,
          headline: broadcastHeadline,
          body_text: broadcastBody,
          cta_text: broadcastCta,
          recipient_ids: broadcastSelected,
        }),
      });
      if (r.ok) {
        const data = await r.json();
        toast.success(`Correo enviado a ${data.sent_to} usuarios`);
        setBroadcastBook(null);
      } else {
        toast.error('Error al enviar');
      }
    } catch (e) { console.error('Broadcast failed:', e); toast.error('Error al enviar'); }
    setBroadcastSending(false);
  };

  const handleSaveEdit = async () => {
    if (!editingBook) return;
    try {
      // Upload new PDF if changed
      if (editingBook.newPdfFile) {
        toast.info('Subiendo nuevo PDF...', { duration: 120000, id: 'pdf-upload' });
        const initRes = await authFetch(`${API}/admin/upload/init`, { method: 'POST' });
        if (!initRes.ok) throw new Error('Error al iniciar subida');
        const { upload_id } = await initRes.json();
        const totalChunks = Math.ceil(editingBook.newPdfFile.size / CHUNK_SIZE);
        for (let i = 0; i < totalChunks; i++) {
          const file = editingBook.newPdfFile;
          const idx = i;
          const makeForm = () => {
            const fd = new FormData();
            fd.append('upload_id', upload_id);
            fd.append('chunk_index', idx.toString());
            fd.append('chunk', file.slice(idx * CHUNK_SIZE, Math.min((idx + 1) * CHUNK_SIZE, file.size)), 'chunk');
            return fd;
          };
          await uploadChunkWithRetry(`${API}/admin/upload/chunk`, makeForm);
          toast.info(`Subiendo PDF... ${Math.round(((i + 1) / totalChunks) * 100)}%`, { duration: 120000, id: 'pdf-upload' });
        }
        toast.info('Procesando páginas del PDF...', { duration: 120000, id: 'pdf-upload' });
        const pdfRes = await authFetch(`${API}/admin/books/${editingBook.slug}/pdf`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ upload_id }),
        });
        if (!pdfRes.ok) {
          let errMsg = 'Error al procesar PDF';
          try { const d = await pdfRes.json(); errMsg = d.detail || JSON.stringify(d); } catch(e) {}
          throw new Error(errMsg);
        }
        // Poll for completion
        await pollBookStatus(editingBook.slug);
        toast.dismiss('pdf-upload');
      }

      // Upload new cover if changed
      if (editingBook.newCoverFile) {
        const coverForm = new FormData();
        coverForm.append('slug', editingBook.slug);
        coverForm.append('cover', editingBook.newCoverFile);
        const coverRes = await authFetch(`${API}/admin/books/${editingBook.slug}/cover`, {
          method: 'POST', body: coverForm,
        });
        if (!coverRes.ok) throw new Error('Error al subir portada');
      }

      const res = await authFetch(`${API}/admin/books/${editingBook.slug}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: editingBook.newTitle,
          description: editingBook.newDescription,
          price: editingBook.newPrice,
          category: editingBook.newCategory,
          characters: editingBook.newCharacters,
        }),
      });
      if (!res.ok) throw new Error('Error al guardar datos');
      toast.success('Libro actualizado');
      setEditingBook(null);
      fetchBooks();
    } catch (err) {
      toast.dismiss('pdf-upload');
      toast.error(err.message || 'Error al actualizar');
      console.error('Edit error:', err);
    }
  };

  if (loading) {
    return <div className="min-h-dvh flex items-center justify-center bg-background"><Loader2 className="size-6 animate-spin text-muted-foreground" /></div>;
  }

  if (!user) {
    return (
      <div className="min-h-dvh flex flex-col items-center justify-center bg-background px-4">
        <Toaster position="top-center" />
        <div className="text-center max-w-sm">
          <h1 className="text-2xl font-bold mb-2">Panel de Administración</h1>
          <p className="text-muted-foreground text-sm mb-8">Museo de la Fe</p>
          <button onClick={handleLogin} className="w-full flex items-center justify-center gap-3 h-12 rounded-xl bg-foreground text-background font-medium hover:bg-foreground/90 transition-colors">
            <svg className="size-5" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
            Iniciar sesión con Google
          </button>
          <button onClick={() => navigate('/')} className="mt-4 text-sm text-muted-foreground hover:text-foreground">Volver a la tienda</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-dvh bg-background">
      <Toaster position="top-center" />

      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-black/[0.04]">
        <div className="flex items-center justify-between px-6 h-14 max-w-6xl mx-auto">
          <div className="flex items-center gap-3">
            <span className="text-sm font-bold">Admin</span>
            <span className="text-xs text-muted-foreground">Museo de la Fe</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground hidden sm:block">{user.email}</span>
            <button onClick={() => navigate('/')} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1">
              <ExternalLink className="size-3" /> Ver tienda
            </button>
            <button onClick={handleLogout} className="text-muted-foreground hover:text-foreground p-1.5 rounded-lg hover:bg-secondary">
              <LogOut className="size-4" />
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Tabs */}
        <div className="flex items-center gap-1 mb-6 border-b border-border overflow-x-auto no-scrollbar">
          {[
            { id: 'dashboard', label: 'Dashboard' },
            { id: 'books', label: 'Libros' },
            { id: 'orders', label: 'Órdenes' },
            { id: 'clients', label: 'Clientes' },
            { id: 'reviews', label: 'Reseñas' },
            { id: 'stack', label: 'Tech Stack' },
          ].map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === tab.id ? 'border-foreground text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
              {tab.label}
            </button>
          ))}
        </div>

        {/* === TAB: Dashboard === */}
        {activeTab === 'dashboard' && (
          <div className="space-y-8">
            <div>
              <h2 className="text-xl font-bold">Dashboard</h2>
              <p className="text-xs text-muted-foreground/60 mt-1">Una vista clara del crecimiento de Museo de la Fe</p>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {[
                { label: 'Ingresos', value: `$${stats?.total_revenue?.toFixed(2) || '0'}`, sub: `${stats?.total_orders || 0} órdenes` },
                { label: 'Ticket Promedio', value: `$${stats?.aov?.toFixed(2) || '0'}`, sub: 'por orden' },
                { label: 'LTV Estimado', value: `$${stats?.ltv?.toFixed(2) || '0'}`, sub: 'por cliente' },
                { label: 'Clientes', value: stats?.total_users || 0, sub: `${stats?.returning_customers || 0} recurrentes (${stats?.returning_rate || 0}%)` },
              ].map((kpi, i) => (
                <div key={i} className="bg-secondary/30 rounded-2xl p-5 border border-border/30">
                  <p className="text-[10px] text-muted-foreground/50 uppercase tracking-wider font-medium mb-2">{kpi.label}</p>
                  <p className="text-2xl font-bold tracking-tight">{kpi.value}</p>
                  <p className="text-[11px] text-muted-foreground/50 mt-1">{kpi.sub}</p>
                </div>
              ))}
            </div>

            {stats?.daily_revenue && (
              <div className="bg-secondary/20 rounded-2xl p-6 border border-border/30">
                <h3 className="text-sm font-semibold mb-1">Ingresos</h3>
                <p className="text-[10px] text-muted-foreground/40 mb-5">Últimos 30 días</p>
                <div className="flex items-end gap-[3px] h-28">
                  {stats.daily_revenue.map((d, i) => {
                    const max = Math.max(...stats.daily_revenue.map(x => x.revenue), 1);
                    const h = (d.revenue / max) * 100;
                    return (
                      <div key={i} className="flex-1 group relative">
                        <div className="bg-foreground/10 hover:bg-foreground/25 transition-colors rounded-t" style={{ height: `${Math.max(h, 3)}%` }} />
                        <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 hidden group-hover:block bg-foreground text-background text-[9px] px-2 py-1 rounded-lg whitespace-nowrap z-10 shadow-lg">
                          <p className="font-semibold">${d.revenue.toFixed(2)}</p>
                          <p className="opacity-60">{d.orders || 0} órdenes · {d.date.slice(5)}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="grid lg:grid-cols-2 gap-4">
              <div className="bg-secondary/20 rounded-2xl border border-border/30 overflow-hidden">
                <div className="px-5 pt-5 pb-3"><h3 className="text-sm font-semibold">Rendimiento por librito</h3></div>
                {stats?.top_books?.length > 0 ? (
                  <div className="divide-y divide-border/30">
                    {stats.top_books.map((b, i) => (
                      <div key={i} className="flex items-center gap-4 px-5 py-3">
                        <span className="text-lg font-bold text-muted-foreground/20 w-6">{i + 1}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{b.title}</p>
                          <p className="text-[10px] text-muted-foreground/40">{b.sold} vendidos · {b.pct}% del revenue</p>
                        </div>
                        <span className="text-sm font-semibold text-emerald-600">${b.revenue?.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-sm text-muted-foreground/40 text-center py-8">Sin ventas aún</p>}
              </div>

              <div className="space-y-4">
                <div className="bg-secondary/20 rounded-2xl p-5 border border-border/30">
                  <h3 className="text-sm font-semibold mb-3">Progreso de colección</h3>
                  <div className="grid grid-cols-2 gap-3">
                    <div><p className="text-xl font-bold">{stats?.books_per_customer || 0}</p><p className="text-[10px] text-muted-foreground/40">libros/cliente</p></div>
                    <div><p className="text-xl font-bold">{stats?.avg_completion || 0}%</p><p className="text-[10px] text-muted-foreground/40">colección completada</p></div>
                    <div><p className="text-xl font-bold">{stats?.collection_data?.['3_plus'] || 0}</p><p className="text-[10px] text-muted-foreground/40">con 3+ libros</p></div>
                    <div><p className="text-xl font-bold">{stats?.collection_data?.['5_plus'] || 0}</p><p className="text-[10px] text-muted-foreground/40">con 5+ libros</p></div>
                  </div>
                </div>
                {stats?.acquisition?.length > 0 && (
                  <div className="bg-secondary/20 rounded-2xl p-5 border border-border/30">
                    <h3 className="text-sm font-semibold mb-3">¿Cómo nos encuentran?</h3>
                    <div className="space-y-2">
                      {stats.acquisition.map((a, i) => {
                        const labels = { redes_sociales: 'Redes sociales', recomendacion: 'Recomendación', google: 'Google', iglesia: 'Iglesia', otro: 'Otro' };
                        const total = stats.acquisition.reduce((s, x) => s + x.count, 0);
                        const pct = Math.round(a.count / total * 100);
                        return (
                          <div key={i}><div className="flex justify-between mb-1"><span className="text-xs">{labels[a.source] || a.source}</span><span className="text-xs text-muted-foreground/50">{a.count} · {pct}%</span></div><div className="h-1.5 bg-border/30 rounded-full overflow-hidden"><div className="h-full bg-foreground/20 rounded-full" style={{ width: `${pct}%` }} /></div></div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {(stats?.total_gifts > 0) && (
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-secondary/20 rounded-2xl p-5 border border-border/30"><p className="text-[10px] text-muted-foreground/50 uppercase tracking-wider font-medium mb-2">Regalos creados</p><p className="text-2xl font-bold">{stats.total_gifts}</p></div>
                <div className="bg-secondary/20 rounded-2xl p-5 border border-border/30"><p className="text-[10px] text-muted-foreground/50 uppercase tracking-wider font-medium mb-2">Regalos canjeados</p><p className="text-2xl font-bold">{stats.redeemed_gifts}</p></div>
              </div>
            )}

            {stats?.insights?.length > 0 && (
              <div className="bg-amber-50/50 border border-amber-200/30 rounded-2xl p-5">
                <h3 className="text-sm font-semibold mb-3">Insights</h3>
                <div className="space-y-2">
                  {stats.insights.map((insight, i) => (<p key={i} className="text-sm text-foreground/70 flex items-start gap-2"><span className="text-amber-500 shrink-0 mt-0.5">→</span> {insight}</p>))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* === TAB: Órdenes === */}
        {activeTab === 'orders' && (
          <div className="space-y-6">
            {/* Summary cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {(() => {
                const totalRev = orders.reduce((s, o) => s + (o.total || 0), 0);
                const uniqueCustomers = new Set(orders.map(o => o.user_email)).size;
                const avgOrder = orders.length ? totalRev / orders.length : 0;
                return [
                  { label: 'Ingresos totales', value: `$${totalRev.toFixed(2)}` },
                  { label: 'Órdenes', value: orders.length },
                  { label: 'Clientes únicos', value: uniqueCustomers },
                  { label: 'Promedio/orden', value: `$${avgOrder.toFixed(2)}` },
                ].map((s, i) => (
                  <div key={i} data-testid={`order-stat-${i}`} className="bg-secondary/20 rounded-2xl p-4 border border-border/30">
                    <p className="text-[10px] text-muted-foreground/50 uppercase tracking-wider font-medium mb-1">{s.label}</p>
                    <p className="text-xl font-bold">{s.value}</p>
                  </div>
                ));
              })()}
            </div>

            {/* Orders table */}
            <div>
              <h3 className="text-sm font-semibold mb-3">Órdenes de compra</h3>
              <div className="border border-border rounded-xl overflow-hidden">
                {orders.length === 0 ? (
                  <p className="text-sm text-muted-foreground/50 text-center py-12">No hay órdenes aún</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border bg-secondary/30 text-xs text-muted-foreground">
                          <th className="text-left px-4 py-2.5 font-medium">Orden</th>
                          <th className="text-left px-4 py-2.5 font-medium">Cliente</th>
                          <th className="text-left px-4 py-2.5 font-medium">Libros</th>
                          <th className="text-left px-4 py-2.5 font-medium">Total</th>
                          <th className="text-left px-4 py-2.5 font-medium">Estado</th>
                          <th className="text-left px-4 py-2.5 font-medium">PayPal</th>
                          <th className="text-left px-4 py-2.5 font-medium">Fecha</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border/50">
                        {orders.map((o) => (
                          <tr key={o.order_id} data-testid={`order-row-${o.order_id}`} className="hover:bg-secondary/20 transition-colors">
                            <td className="px-4 py-2.5 text-xs font-mono text-muted-foreground">{o.order_id?.slice(4, 16)}</td>
                            <td className="px-4 py-2.5">
                              <p className="font-medium text-xs">{o.user_name}</p>
                              <p className="text-[10px] text-muted-foreground/50">{o.user_email}</p>
                            </td>
                            <td className="px-4 py-2.5 text-xs text-muted-foreground max-w-[200px]">{o.items?.map(i => i.title).join(', ')}</td>
                            <td className="px-4 py-2.5 font-semibold text-emerald-600">${o.total?.toFixed(2)}</td>
                            <td className="px-4 py-2.5">
                              <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium ${
                                o.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                                o.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                                o.status === 'refunded' ? 'bg-red-100 text-red-700' :
                                'bg-secondary text-muted-foreground'
                              }`}>
                                {o.status === 'completed' ? 'Completada' : o.status === 'pending' ? 'Pendiente' : o.status === 'refunded' ? 'Reembolsada' : o.status || 'N/A'}
                              </span>
                            </td>
                            <td className="px-4 py-2.5 text-[10px] font-mono text-muted-foreground/40 truncate max-w-[100px]">{o.paypal_order_id}</td>
                            <td className="px-4 py-2.5 text-muted-foreground/60 text-xs whitespace-nowrap">
                              {o.createdAt ? new Date(o.createdAt).toLocaleDateString('es', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

            {/* Gifts section */}
            <div>
              <h3 className="text-sm font-semibold mb-3">Regalos</h3>
              <div className="border border-border rounded-xl overflow-hidden">
                {gifts.length === 0 ? (
                  <p className="text-sm text-muted-foreground/50 text-center py-12">No hay regalos creados</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border bg-secondary/30 text-xs text-muted-foreground">
                          <th className="text-left px-4 py-2.5 font-medium">ID</th>
                          <th className="text-left px-4 py-2.5 font-medium">De</th>
                          <th className="text-left px-4 py-2.5 font-medium">Libros</th>
                          <th className="text-left px-4 py-2.5 font-medium">Mensaje</th>
                          <th className="text-left px-4 py-2.5 font-medium">Estado</th>
                          <th className="text-left px-4 py-2.5 font-medium">Recibido por</th>
                          <th className="text-left px-4 py-2.5 font-medium">Fecha</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border/50">
                        {gifts.map((g) => (
                          <tr key={g.gift_id} data-testid={`gift-row-${g.gift_id}`} className="hover:bg-secondary/20 transition-colors">
                            <td className="px-4 py-2.5 text-xs font-mono text-muted-foreground">{g.gift_id?.slice(5, 15)}</td>
                            <td className="px-4 py-2.5 text-xs">{g.buyer_name || g.buyer_user_id}</td>
                            <td className="px-4 py-2.5 text-xs text-muted-foreground">{g.books?.map(b => b.title).join(', ')}</td>
                            <td className="px-4 py-2.5 text-xs text-muted-foreground/60 max-w-[180px] truncate">{g.message}</td>
                            <td className="px-4 py-2.5">
                              <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium ${g.redeemed ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                                {g.redeemed ? 'Canjeado' : 'Pendiente'}
                              </span>
                            </td>
                            <td className="px-4 py-2.5 text-muted-foreground/60 text-xs whitespace-nowrap">
                              {g.createdAt ? new Date(g.createdAt).toLocaleDateString('es', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* === TAB: Clientes === */}
        {activeTab === 'clients' && (
          <div className="border border-border rounded-xl overflow-hidden">
            {clients.length === 0 ? (
              <p className="text-sm text-muted-foreground/50 text-center py-12">No hay usuarios registrados</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-secondary/30 text-xs text-muted-foreground">
                      <th className="w-8 px-2 py-2.5"></th>
                      <th className="text-left px-4 py-2.5 font-medium">Usuario</th>
                      <th className="text-left px-4 py-2.5 font-medium">Email</th>
                      <th className="text-left px-4 py-2.5 font-medium">Libros</th>
                      <th className="text-left px-4 py-2.5 font-medium">Órdenes</th>
                      <th className="text-left px-4 py-2.5 font-medium">Gastado</th>
                      <th className="text-left px-4 py-2.5 font-medium">Origen</th>
                      <th className="text-left px-4 py-2.5 font-medium">Registro</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/50">
                    {clients.map((c) => (
                      <React.Fragment key={c.user_id}>
                        <tr
                          data-testid={`client-row-${c.user_id}`}
                          onClick={() => toggleUserDetail(c.user_id)}
                          className="hover:bg-secondary/20 transition-colors cursor-pointer"
                        >
                          <td className="px-2 py-2.5 text-center text-muted-foreground/50">
                            {expandedUserId === c.user_id ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
                          </td>
                          <td className="px-4 py-2.5">
                            <div className="flex items-center gap-2">
                              {c.picture ? <img src={c.picture} alt="" className="size-6 rounded-full" /> : <div className="size-6 rounded-full bg-secondary" />}
                              <span className="font-medium">{c.name}</span>
                            </div>
                          </td>
                          <td className="px-4 py-2.5 text-muted-foreground text-xs">{c.email}</td>
                          <td className="px-4 py-2.5 text-muted-foreground">{c.books_owned}</td>
                          <td className="px-4 py-2.5 text-muted-foreground">{c.total_orders}</td>
                          <td className="px-4 py-2.5 font-semibold text-emerald-600">${c.total_spent?.toFixed(2)}</td>
                          <td className="px-4 py-2.5 text-xs text-muted-foreground">{
                            { redes_sociales: 'Redes sociales', recomendacion: 'Recomendación', google: 'Google', iglesia: 'Iglesia', otro: 'Otro' }[c.acquisition_source] || '-'
                          }</td>
                          <td className="px-4 py-2.5 text-muted-foreground/60 text-xs whitespace-nowrap">{c.created_at ? new Date(c.created_at).toLocaleDateString('es', { day: 'numeric', month: 'short', year: 'numeric' }) : '-'}</td>
                        </tr>
                        {expandedUserId === c.user_id && (
                          <tr>
                            <td colSpan={8} className="bg-secondary/10 px-6 py-4">
                              {loadingDetail ? (
                                <div className="flex items-center justify-center gap-2 py-6">
                                  <Loader2 className="size-4 animate-spin text-muted-foreground" />
                                  <span className="text-sm text-muted-foreground">Cargando detalles...</span>
                                </div>
                              ) : userDetail ? (
                                <div data-testid={`client-detail-${c.user_id}`} className="space-y-4">
                                  {/* Summary row */}
                                  <div className="flex flex-wrap gap-4 text-xs">
                                    <div className="flex items-center gap-1.5 bg-background border border-border rounded-lg px-3 py-1.5">
                                      <BookOpen className="size-3.5 text-blue-500" />
                                      <span className="text-muted-foreground">Lecturas totales:</span>
                                      <span className="font-bold">{userDetail.total_reads}</span>
                                    </div>
                                    <div className="flex items-center gap-1.5 bg-background border border-border rounded-lg px-3 py-1.5">
                                      <Heart className="size-3.5 text-rose-400" />
                                      <span className="text-muted-foreground">Wishlist:</span>
                                      <span className="font-bold">{userDetail.wishlist_count}</span>
                                    </div>
                                    {userDetail.gift_survey && (
                                      <div className="flex items-center gap-1.5 bg-background border border-border rounded-lg px-3 py-1.5">
                                        <Gift className="size-3.5 text-purple-500" />
                                        <span className="text-muted-foreground">Encuesta regalo:</span>
                                        <span className="font-bold capitalize">{userDetail.gift_survey.experience || '-'}</span>
                                      </div>
                                    )}
                                  </div>

                                  {/* Owned books with read stats */}
                                  {userDetail.books.length > 0 && (
                                    <div>
                                      <h4 className="text-xs font-semibold text-muted-foreground mb-2 flex items-center gap-1.5">
                                        <BarChart3 className="size-3.5" /> Libros y lecturas
                                      </h4>
                                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                                        {userDetail.books.map((b) => (
                                          <div key={b.slug} data-testid={`user-book-${b.slug}`} className="flex items-center gap-3 bg-background border border-border rounded-lg p-2.5">
                                            <div className="shrink-0 w-10 h-14 rounded overflow-hidden bg-secondary">
                                              {b.imageFolder && <img src={`${R2_URL}/books/${b.imageFolder}/cover.webp`} alt={b.title} className="w-full h-full object-cover" />}
                                            </div>
                                            <div className="min-w-0 flex-1">
                                              <p className="text-xs font-semibold truncate">{b.title}</p>
                                              <div className="flex items-center gap-3 mt-1 text-[11px] text-muted-foreground">
                                                <span className="flex items-center gap-1">
                                                  <Eye className="size-3" />
                                                  {b.read_count || 0} {b.read_count === 1 ? 'vez' : 'veces'}
                                                </span>
                                                {b.last_read && (
                                                  <span className="flex items-center gap-1">
                                                    <Calendar className="size-3" />
                                                    {new Date(b.last_read).toLocaleDateString('es', { day: 'numeric', month: 'short' })}
                                                  </span>
                                                )}
                                              </div>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {/* Recent orders */}
                                  {userDetail.orders.length > 0 && (
                                    <div>
                                      <h4 className="text-xs font-semibold text-muted-foreground mb-2 flex items-center gap-1.5">
                                        <DollarSign className="size-3.5" /> Órdenes recientes
                                      </h4>
                                      <div className="space-y-1">
                                        {userDetail.orders.slice(0, 5).map((o) => (
                                          <div key={o.order_id} className="flex items-center justify-between bg-background border border-border rounded-lg px-3 py-2 text-xs">
                                            <div className="flex items-center gap-3">
                                              <span className="text-muted-foreground font-mono">{o.order_id?.slice(0, 8)}</span>
                                              <span>{o.items?.map(i => i.title || i.slug).join(', ') || '-'}</span>
                                            </div>
                                            <div className="flex items-center gap-3">
                                              <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${o.status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                                                {o.status === 'paid' ? 'Pagado' : o.status}
                                              </span>
                                              <span className="font-semibold">${o.total?.toFixed(2)}</span>
                                              <span className="text-muted-foreground/60">{o.created_at ? new Date(o.created_at).toLocaleDateString('es', { day: 'numeric', month: 'short' }) : ''}</span>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {userDetail.books.length === 0 && userDetail.orders.length === 0 && (
                                    <p className="text-xs text-muted-foreground/50 text-center py-4">Este usuario aún no tiene actividad registrada</p>
                                  )}
                                </div>
                              ) : (
                                <p className="text-xs text-muted-foreground/50 text-center py-4">No se pudo cargar el detalle</p>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* === TAB: Reseñas === */}
        {activeTab === 'reviews' && (
          <div className="border border-border rounded-xl overflow-hidden">
            {reviews.length === 0 ? (
              <p className="text-sm text-muted-foreground/50 text-center py-12">No hay reseñas aún</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-secondary/30 text-xs text-muted-foreground">
                      <th className="text-left px-4 py-2.5 font-medium">Libro</th>
                      <th className="text-left px-4 py-2.5 font-medium">Nombre</th>
                      <th className="text-left px-4 py-2.5 font-medium">Correo</th>
                      <th className="text-left px-4 py-2.5 font-medium">Calificación</th>
                      <th className="text-left px-4 py-2.5 font-medium">Comentario</th>
                      <th className="text-left px-4 py-2.5 font-medium">Fecha</th>
                      <th className="text-right px-4 py-2.5 font-medium w-16"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/50">
                    {reviews.map((rev) => (
                      <tr key={rev.review_id} className="hover:bg-secondary/20 transition-colors">
                        <td className="px-4 py-2.5 font-medium truncate max-w-[150px]">{rev.book_title}</td>
                        <td className="px-4 py-2.5 text-muted-foreground">{rev.name}</td>
                        <td className="px-4 py-2.5 text-muted-foreground text-xs">{rev.email}</td>
                        <td className="px-4 py-2.5"><div className="flex gap-0.5">{[1,2,3,4,5].map(s => (<Star key={s} className={`size-3 ${s <= rev.rating ? 'text-amber-400 fill-amber-400' : 'text-border'}`} />))}</div></td>
                        <td className="px-4 py-2.5 text-muted-foreground max-w-[250px] truncate">{rev.text}</td>
                        <td className="px-4 py-2.5 text-muted-foreground/60 text-xs whitespace-nowrap">{rev.createdAt ? new Date(rev.createdAt).toLocaleDateString('es', { day: 'numeric', month: 'short', year: 'numeric' }) : '-'}</td>
                        <td className="px-4 py-2.5 text-right"><button onClick={() => handleDeleteReview(rev.review_id)} className="p-1.5 rounded-lg text-muted-foreground/30 hover:text-red-500 hover:bg-red-50 transition-colors"><Trash2 className="size-3.5" /></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}


        {/* === TAB: Tech Stack === */}
        {activeTab === 'stack' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-bold">Tech Stack</h2>
              <p className="text-xs text-muted-foreground/60 mt-1">Infraestructura y herramientas de Museo de la Fe</p>
            </div>
            <div className="space-y-3">
              {[
                { name: 'React + Tailwind CSS', type: 'Frontend', cost: '$0', desc: 'SPA con estantes 3D, flipbook StPageFlip, búsqueda con filtros, carrito, biblioteca, sistema de regalos. Magic UI (SparklesText, HyperText, SlotCounter). Font Open Runde' },
                { name: 'FastAPI (Python)', type: 'Backend', cost: '$0', desc: 'API REST con 40+ endpoints: auth, CRUD libros, procesamiento PDFs (PyMuPDF + PIL), carrito, wishlist, checkout, órdenes, reseñas, regalos, stats. Rate limiting con slowapi' },
                { name: 'MongoDB', type: 'Base de datos', cost: '$0', desc: '9 colecciones: users, books, orders, carts, wishlists, libraries, reviews, gifts, user_sessions' },
                { name: 'Google OAuth 2.0', type: 'Autenticación', cost: '$0', desc: 'Login con Google para compradores y admin. admin@museodelafe.com = rol admin, resto = compradores' },
                { name: 'PayPal Checkout SDK', type: 'Pagos', cost: '~3.49%/venta', desc: 'PayPal, tarjeta, Apple Pay, Google Pay, Venmo. Verificación server-side antes de dar acceso al libro' },
                { name: 'Cloudflare R2', type: 'Almacenamiento', cost: '~$0.00', desc: 'Portadas WebP + páginas flipbook. CDN global para Latinoamérica y USA. Upload automático al publicar' },
                { name: 'Claude API (Anthropic)', type: 'IA Backend', cost: '~$0.01/libro', desc: 'Analiza PDFs y auto-genera título + descripción SEO de 240 caracteres al subir libro nuevo' },
                { name: 'Google Workspace + Gemini', type: 'IA Contenido', cost: '$12.60/mes', desc: 'Generación de libritos con Gemini Storybooks (PDFs ilustrados). Correo empresarial admin@museodelafe.com' },
                { name: 'Emergent', type: 'Hosting', cost: '~$25/mes', desc: 'Servidor con frontend, backend y MongoDB. Dominio museodelafe.com conectado' },
                { name: 'JSON-LD + Sitemap', type: 'SEO', cost: '$0', desc: 'Schema Organization, Product, FAQPage. Sitemap dinámico. Meta tags OG/Twitter auto-generados por libro' },
              ].map((tech, i) => (
                <div key={i} className="bg-secondary/20 rounded-2xl p-5 border border-border/30 flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-semibold">{tech.name}</span>
                      <span className="text-[9px] px-2 py-0.5 rounded-full bg-foreground/5 text-muted-foreground/60 font-medium">{tech.type}</span>
                    </div>
                    <p className="text-xs text-muted-foreground/60 leading-relaxed">{tech.desc}</p>
                  </div>
                  <span className="text-sm font-semibold text-emerald-600 shrink-0">{tech.cost}</span>
                </div>
              ))}
            </div>
            <div className="bg-secondary/20 rounded-2xl p-5 border border-border/30">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm font-semibold">Costo fijo mensual</p>
                  <p className="text-xs text-muted-foreground/50 mt-0.5">Emergent $25 + Google Workspace $12.60</p>
                </div>
                <span className="text-2xl font-bold">$37.60</span>
              </div>
            </div>
          </div>
        )}


        {/* === TAB: Libros === */}
        {activeTab === 'books' && (
        <>

        {/* Title + Add button */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-xl font-bold">Mis Libros</h1>
          <button onClick={() => { setShowForm(!showForm); setPreviewData(null); }} className="flex items-center gap-2 h-10 px-4 rounded-xl bg-foreground text-background text-sm font-medium hover:bg-foreground/90 transition-colors">
            <Plus className="size-4" /> Nuevo Libro
          </button>
        </div>

        {/* Upload form */}
        {showForm && (
          <div className="bg-secondary/30 rounded-2xl p-6 mb-8 border border-border">
            <h2 className="text-sm font-semibold mb-4">Publicar nuevo libro</h2>
            <form onSubmit={handleUpload} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Título</label>
                  <input value={title} onChange={(e) => setTitle(e.target.value)} className="w-full h-10 px-3 rounded-xl bg-background border border-border text-sm outline-none focus:ring-2 focus:ring-foreground/20" placeholder="Nombre del libro" required />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Precio (USD)</label>
                  <input type="number" step="0.01" min="0" value={price} onChange={(e) => setPrice(e.target.value)} className="w-full h-10 px-3 rounded-xl bg-background border border-border text-sm outline-none focus:ring-2 focus:ring-foreground/20" placeholder="2.99" required />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Categoría</label>
                  <select value={category} onChange={(e) => setCategory(e.target.value)} className="w-full h-10 px-3 rounded-xl bg-background border border-border text-sm outline-none focus:ring-2 focus:ring-foreground/20">
                    <option value="Nuevo Testamento">Nuevo Testamento</option>
                    <option value="Antiguo Testamento">Antiguo Testamento</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Descripción</label>
                  <textarea value={description} onChange={(e) => setDescription(e.target.value)} className="w-full h-10 px-3 py-2 rounded-xl bg-background border border-border text-sm outline-none focus:ring-2 focus:ring-foreground/20 resize-none" placeholder="Breve descripción..." />
                </div>
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Personajes</label>
                <div className="relative">
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {characters.map(c => (
                      <span key={c} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-secondary text-xs font-medium">
                        {c}
                        <button type="button" onClick={() => removeCharacter(c)} className="text-muted-foreground/50 hover:text-foreground ml-0.5">&times;</button>
                      </span>
                    ))}
                  </div>
                  <input
                    value={charInput}
                    onChange={(e) => setCharInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Tab' || e.key === ',') { e.preventDefault(); addCharacter(charInput); } }}
                    onFocus={() => setCharFocused(true)}
                    onBlur={() => setTimeout(() => setCharFocused(false), 200)}
                    className="w-full h-10 px-3 rounded-xl bg-background border border-border text-sm outline-none focus:ring-2 focus:ring-foreground/20"
                    placeholder="Escribe un nombre y presiona Enter o Tab"
                  />
                  {charFocused && charSuggestions.length > 0 && (
                    <div className="absolute z-20 left-0 right-0 mt-1 bg-background border border-border rounded-xl shadow-lg max-h-40 overflow-y-auto">
                      {charSuggestions.map(name => {
                        const dbChar = allCharacters.find(c => c.name === name);
                        return (
                          <button key={name} type="button" onClick={() => addCharacter(name)} className="w-full text-left px-3 py-2 text-sm hover:bg-secondary flex justify-between">
                            <span>{name}</span>
                            {dbChar && <span className="text-xs text-muted-foreground">{dbChar.count} libros</span>}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">PDF del libro</label>
                <input type="file" accept=".pdf" onChange={(e) => setPdfFile(e.target.files[0])} className="w-full h-10 px-3 rounded-xl bg-background border border-border text-sm file:mr-3 file:border-0 file:bg-transparent file:text-sm file:font-medium" required />
                {pdfFile && <p className="text-xs text-muted-foreground mt-1">{pdfFile.name} ({(pdfFile.size / 1024 / 1024).toFixed(1)} MB)</p>}
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Foto de portada (JPG/PNG)</label>
                <div className="flex items-start gap-4">
                  <input type="file" accept="image/*" onChange={(e) => {
                    const file = e.target.files[0];
                    setCoverFile(file);
                    if (file) setCoverPreview(URL.createObjectURL(file));
                  }} className="flex-1 h-10 px-3 rounded-xl bg-background border border-border text-sm file:mr-3 file:border-0 file:bg-transparent file:text-sm file:font-medium" required />
                  {coverPreview && (
                    <div className="w-14 h-[74px] rounded-lg overflow-hidden shrink-0 shadow-sm border border-border">
                      <img src={coverPreview} alt="Portada" className="w-full h-full object-cover" />
                    </div>
                  )}
                </div>
              </div>

              {uploading && (
                <div className="space-y-2">
                  <div className="h-2 bg-secondary rounded-full overflow-hidden">
                    <div className="h-full bg-foreground rounded-full transition-all duration-300" style={{ width: `${uploadProgress}%` }} />
                  </div>
                  <p className="text-xs text-muted-foreground text-center">
                    {uploadProgress < 75 ? 'Subiendo PDF...' : uploadProgress < 85 ? 'Enviando al servidor...' : uploadProgress < 95 ? 'Procesando páginas del PDF...' : uploadProgress < 100 ? 'Subiendo imágenes al CDN...' : '¡Listo!'} {uploadProgress}%
                  </p>
                </div>
              )}

              {/* Preview section */}
              {previewData && (
                <div className="bg-background rounded-xl border border-border p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <Eye className="size-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Previsualización</span>
                  </div>
                  <div className="flex gap-6">
                    <div className="w-24 h-32 shrink-0 rounded-lg overflow-hidden shadow-sm">
                      {previewData.coverUrl ? (
                        <img src={previewData.coverUrl} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <BookCover book={previewData} hideTitle />
                      )}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-lg">{previewData.title}</h3>
                      <span className="inline-block mt-1 px-3 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold border border-emerald-200">${previewData.price.toFixed(2)} USD</span>
                      <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{previewData.description || 'Sin descripción'}</p>
                      {previewData.pdfName && <p className="text-[10px] text-muted-foreground/50 mt-2">PDF: {previewData.pdfName} ({previewData.pdfSize} MB)</p>}
                    </div>
                  </div>
                </div>
              )}

              <div className="flex gap-3">
                <button type="button" onClick={handlePreview} disabled={uploading} className="flex items-center gap-2 h-10 px-4 rounded-xl border border-border text-sm font-medium hover:bg-secondary transition-colors disabled:opacity-50">
                  <Eye className="size-4" /> Previsualizar
                </button>
                <button type="submit" disabled={uploading} className="flex items-center gap-2 h-10 px-5 rounded-xl bg-foreground text-background text-sm font-medium hover:bg-foreground/90 transition-colors disabled:opacity-50">
                  {uploading ? <Loader2 className="size-4 animate-spin" /> : <Upload className="size-4" />}
                  {uploading ? 'Publicando...' : 'Publicar Libro'}
                </button>
                <button type="button" onClick={resetForm} className="h-10 px-4 rounded-xl text-sm text-muted-foreground hover:bg-secondary">Cancelar</button>
              </div>
            </form>
          </div>
        )}

        {/* Edit modal */}
        {editingBook && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setEditingBook(null)}>
            <div className="bg-background rounded-2xl p-6 max-w-md w-full shadow-xl" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-lg font-bold">Editar Libro</h2>
                <button onClick={() => setEditingBook(null)} className="p-1 rounded-lg hover:bg-secondary"><X className="size-4" /></button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Título</label>
                  <input value={editingBook.newTitle} onChange={(e) => setEditingBook({ ...editingBook, newTitle: e.target.value })} className="w-full h-10 px-3 rounded-xl bg-secondary border border-border text-sm outline-none focus:ring-2 focus:ring-foreground/20" />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Descripción</label>
                  <textarea value={editingBook.newDescription} onChange={(e) => setEditingBook({ ...editingBook, newDescription: e.target.value })} className="w-full h-24 px-3 py-2 rounded-xl bg-secondary border border-border text-sm outline-none focus:ring-2 focus:ring-foreground/20 resize-none" />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Precio (USD)</label>
                  <input type="number" step="0.01" min="0" value={editingBook.newPrice} onChange={(e) => setEditingBook({ ...editingBook, newPrice: e.target.value })} className="w-full h-10 px-3 rounded-xl bg-secondary border border-border text-sm outline-none focus:ring-2 focus:ring-foreground/20" />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Categoría</label>
                  <select value={editingBook.newCategory || ''} onChange={(e) => setEditingBook({ ...editingBook, newCategory: e.target.value })} className="w-full h-10 px-3 rounded-xl bg-secondary border border-border text-sm outline-none focus:ring-2 focus:ring-foreground/20">
                    <option value="Nuevo Testamento">Nuevo Testamento</option>
                    <option value="Antiguo Testamento">Antiguo Testamento</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Personajes</label>
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {(editingBook.newCharacters || []).map(c => (
                      <span key={c} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-secondary text-xs font-medium">
                        {c}
                        <button type="button" onClick={() => setEditingBook({ ...editingBook, newCharacters: editingBook.newCharacters.filter(x => x !== c) })} className="text-muted-foreground/50 hover:text-foreground ml-0.5">&times;</button>
                      </span>
                    ))}
                  </div>
                  <div className="relative">
                    <input
                      value={editingBook.newCharInput || ''}
                      onChange={(e) => setEditingBook({ ...editingBook, newCharInput: e.target.value })}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === 'Tab' || e.key === ',') {
                          e.preventDefault();
                          const val = (editingBook.newCharInput || '').trim();
                          if (val && !(editingBook.newCharacters || []).includes(val)) {
                            setEditingBook({ ...editingBook, newCharacters: [...(editingBook.newCharacters || []), val], newCharInput: '' });
                          }
                        }
                      }}
                      className="w-full h-10 px-3 rounded-xl bg-secondary border border-border text-sm outline-none focus:ring-2 focus:ring-foreground/20"
                      placeholder="Escribe un nombre y presiona Enter o Tab"
                      onFocus={() => setEditCharFocused(true)}
                      onBlur={() => setTimeout(() => setEditCharFocused(false), 200)}
                    />
                    {editCharFocused && (() => {
                      const et = (editingBook.newTitle || '').toLowerCase();
                      const map = {
                        'jesús': ['Jesús', 'Pedro', 'Juan', 'Santiago', 'Mateo', 'María', 'María Magdalena', 'Judas', 'Tomás', 'Andrés', 'Bartimeo', 'Lázaro', 'Marta', 'Zaqueo', 'Nicodemo', 'Pilato'],
                        'david': ['David', 'Goliat', 'Saúl', 'Jonatán', 'Samuel', 'Betsabé'],
                        'moisés': ['Moisés', 'Aarón', 'Faraón', 'Miriam', 'Séfora', 'Josué'],
                        'noé': ['Noé', 'Sem', 'Cam', 'Jafet'],
                        'daniel': ['Daniel', 'Nabucodonosor', 'Darío'],
                        'abraham': ['Abraham', 'Sara', 'Isaac', 'Agar', 'Ismael'],
                        'josé': ['José', 'Jacob', 'Benjamín', 'Potifar'],
                        'jonás': ['Jonás'],
                        'ruth': ['Ruth', 'Noemí', 'Booz'],
                        'esther': ['Esther', 'Mardoqueo', 'Amán', 'Asuero'],
                        'sansón': ['Sansón', 'Dalila'],
                        'elías': ['Elías', 'Eliseo', 'Jezabel', 'Acab'],
                        'salomón': ['Salomón'],
                        'adán': ['Adán', 'Eva'],
                        'creación': ['Adán', 'Eva', 'Dios'],
                        'nacimiento': ['Jesús', 'María', 'José', 'Herodes', 'Los Reyes Magos', 'Los Pastores'],
                        'bartimeo': ['Jesús', 'Bartimeo'],
                        'jairo': ['Jesús', 'Jairo'],
                        'lázaro': ['Jesús', 'Lázaro', 'Marta', 'María'],
                      };
                      const sug = new Set();
                      Object.entries(map).forEach(([k, v]) => { if (et.includes(k)) v.forEach(n => sug.add(n)); });
                      allCharacters.forEach(c => sug.add(c.name));
                      const filtered = [...sug].filter(n => !(editingBook.newCharacters || []).includes(n));
                      const input = (editingBook.newCharInput || '').toLowerCase();
                      const shown = input.length > 0 ? filtered.filter(n => n.toLowerCase().includes(input)) : filtered.slice(0, 8);
                      if (shown.length === 0) return null;
                      return (
                        <div className="absolute z-20 left-0 right-0 mt-1 bg-background border border-border rounded-xl shadow-lg max-h-32 overflow-y-auto">
                          {shown.map(name => {
                            const dbChar = allCharacters.find(c => c.name === name);
                            return (
                              <button key={name} type="button" onClick={() => setEditingBook({ ...editingBook, newCharacters: [...(editingBook.newCharacters || []), name], newCharInput: '' })} className="w-full text-left px-3 py-2 text-sm hover:bg-secondary flex justify-between">
                                <span>{name}</span>
                                {dbChar && <span className="text-xs text-muted-foreground">{dbChar.count} libros</span>}
                              </button>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Cambiar PDF del libro</label>
                  <input type="file" accept=".pdf" onChange={(e) => {
                    setEditingBook({ ...editingBook, newPdfFile: e.target.files[0] });
                  }} className="w-full h-10 px-3 rounded-xl bg-secondary border border-border text-sm file:mr-3 file:border-0 file:bg-transparent file:text-[11px] file:font-medium" />
                  {editingBook.newPdfFile && <p className="text-xs text-muted-foreground mt-1">{editingBook.newPdfFile.name} ({(editingBook.newPdfFile.size / 1024 / 1024).toFixed(1)} MB)</p>}
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Cambiar portada</label>
                  <div className="flex items-center gap-3">
                    <input type="file" accept="image/*" onChange={(e) => {
                      const file = e.target.files[0];
                      setEditingBook({ ...editingBook, newCoverFile: file, newCoverPreview: file ? URL.createObjectURL(file) : null });
                    }} className="flex-1 h-10 px-3 rounded-xl bg-secondary border border-border text-sm file:mr-3 file:border-0 file:bg-transparent file:text-[11px] file:font-medium" />
                    <div className="w-10 h-14 rounded-md overflow-hidden shrink-0 shadow-sm border border-border bg-cover bg-center"
                      style={{ backgroundImage: `url(${editingBook.newCoverPreview || `${R2_URL}/books/${editingBook.imageFolder}/cover.webp`})` }}
                    />
                  </div>
                </div>
                <div className="flex gap-3 pt-2">
                  <button onClick={handleSaveEdit} className="flex-1 h-10 rounded-xl bg-foreground text-background text-sm font-medium hover:bg-foreground/90">Guardar cambios</button>
                  <button onClick={() => setEditingBook(null)} className="h-10 px-4 rounded-xl text-sm text-muted-foreground hover:bg-secondary">Cancelar</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Books table */}
        {books.length === 0 ? (
          <div className="text-center py-20">
            <BookOpen className="size-10 text-muted-foreground/30 mx-auto mb-3" />
            <p className="text-muted-foreground text-sm">No hay libros publicados aún</p>
            <p className="text-muted-foreground/50 text-xs mt-1">Sube tu primer PDF para comenzar</p>
          </div>
        ) : (
          <div className="border border-border rounded-xl overflow-hidden">
            {/* Table header */}
            <div className="hidden sm:grid sm:grid-cols-[60px_1fr_80px_70px_120px_100px] gap-4 px-4 py-3 bg-secondary/50 text-xs text-muted-foreground font-medium border-b border-border">
              <span>Portada</span>
              <span>Título / Descripción</span>
              <span>Precio</span>
              <span>Páginas</span>
              <span>Fecha</span>
              <span className="text-right">Acciones</span>
            </div>
            {/* Table rows */}
            {books.map((book) => (
              <div key={book.slug} className="grid sm:grid-cols-[60px_1fr_80px_70px_120px_100px] gap-4 items-center px-4 py-3 border-b border-border/50 last:border-0 hover:bg-secondary/20 transition-colors">
                <div
                  className="relative w-10 h-14 rounded-md overflow-hidden shrink-0 shadow-sm group/cover cursor-pointer"
                  onClick={() => setPreviewBook(book)}
                >
                  <div className="w-full h-full bg-cover bg-center" style={{ backgroundImage: `url(${R2_URL}/books/${book.imageFolder}/cover.webp)` }} />
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover/cover:opacity-100 transition-opacity flex items-center justify-center">
                    <Eye className="size-4 text-white" />
                  </div>
                </div>
                <div className="min-w-0">
                  <h3 className="text-sm font-semibold truncate flex items-center gap-1.5">
                    {book.title}
                    {book.hidden && <span className="shrink-0 px-1.5 py-0.5 rounded bg-amber-100 text-amber-600 text-[10px] font-medium">Oculto</span>}
                  </h3>
                  <p className="text-xs text-muted-foreground truncate">
                    {book.category && <span className="text-[10px] font-medium text-foreground/50 mr-1.5">{book.category}</span>}
                    {book.description || 'Sin descripción'}
                  </p>
                </div>
                <div>
                  <span className="text-sm font-semibold text-emerald-600">${book.price?.toFixed(2)}</span>
                </div>
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <FileText className="size-3" /> {book.pageCount}
                  {book.status === 'processing' && <span className="ml-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-[10px] font-medium animate-pulse">Procesando...</span>}
                  {book.status === 'uploading' && <span className="ml-1 px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 text-[10px] font-medium animate-pulse">Subiendo...</span>}
                  {book.status === 'error' && <span className="ml-1 px-1.5 py-0.5 rounded bg-red-100 text-red-700 text-[10px] font-medium">Error</span>}
                </div>
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Calendar className="size-3" />
                  {book.createdAt ? new Date(book.createdAt).toLocaleDateString('es', { day: 'numeric', month: 'short', year: 'numeric' }) : '-'}
                </div>
                <div className="flex items-center justify-end gap-1">
                  <button onClick={() => handleToggleVisibility(book)} data-testid={`toggle-visibility-${book.slug}`} className={`p-2 rounded-lg transition-colors ${book.hidden ? 'text-amber-500 bg-amber-50' : 'text-muted-foreground/50 hover:text-amber-500 hover:bg-amber-50'}`} title={book.hidden ? 'Mostrar' : 'Ocultar'}>
                    {book.hidden ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                  </button>
                  <button onClick={() => openBroadcast(book)} className="p-2 rounded-lg text-muted-foreground/50 hover:text-blue-500 hover:bg-blue-50 transition-colors" title="Enviar correo promocional">
                    <Send className="size-4" />
                  </button>
                  <button onClick={() => handleAdminGift(book)} className="p-2 rounded-lg text-muted-foreground/50 hover:text-amber-500 hover:bg-amber-50 transition-colors" title="Regalar gratis">
                    <Gift className="size-4" />
                  </button>
                  <button onClick={() => handleEdit(book)} className="p-2 rounded-lg text-muted-foreground/50 hover:text-foreground hover:bg-secondary transition-colors" title="Editar">
                    <Pencil className="size-4" />
                  </button>
                  <button onClick={() => handleDelete(book.slug, book.title)} className="p-2 rounded-lg text-muted-foreground/50 hover:text-red-500 hover:bg-red-50 transition-colors" title="Eliminar">
                    <Trash2 className="size-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        </>
        )}
      </div>
      {previewBook && <FlipbookViewer book={{ ...previewBook, imagePages: previewBook.pageCount, imageFolder: previewBook.imageFolder, coverUrl: `${R2_URL}/books/${previewBook.imageFolder}/cover.webp` }} onClose={() => setPreviewBook(null)} />}

      {/* Broadcast Email Modal */}
      {broadcastBook && (
        <>
          <div className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm" onClick={() => setBroadcastBook(null)} />
          <div className="fixed inset-0 z-[61] flex items-center justify-center p-4">
            <div className="bg-background rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
                <div>
                  <h2 className="text-base font-bold">Enviar correo promocional</h2>
                  <p className="text-xs text-muted-foreground">{broadcastBook.title}</p>
                </div>
                <button onClick={() => setBroadcastBook(null)} className="p-2 rounded-lg hover:bg-secondary transition-colors"><X className="size-4" /></button>
              </div>

              <div className="flex-1 overflow-y-auto">
                <div className="flex flex-col lg:flex-row divide-y lg:divide-y-0 lg:divide-x divide-border">
                  {/* Left: Edit fields */}
                  <div className="flex-1 p-6 space-y-4">
                    <div>
                      <label className="text-xs font-medium text-muted-foreground mb-1 block">Asunto del correo</label>
                      <input data-testid="broadcast-subject" value={broadcastSubject} onChange={e => setBroadcastSubject(e.target.value)} className="w-full h-10 px-3 rounded-xl border border-border text-sm outline-none focus:ring-2 focus:ring-foreground/10" />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-muted-foreground mb-1 block">Titular / Hook</label>
                      <input data-testid="broadcast-headline" value={broadcastHeadline} onChange={e => setBroadcastHeadline(e.target.value)} className="w-full h-10 px-3 rounded-xl border border-border text-sm outline-none focus:ring-2 focus:ring-foreground/10" />
                      <p className="text-[10px] text-muted-foreground/40 mt-1">Usa {'{nombre}'} para personalizar</p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-muted-foreground mb-1 block">Cuerpo del mensaje</label>
                      <textarea data-testid="broadcast-body" value={broadcastBody} onChange={e => setBroadcastBody(e.target.value)} rows={6} className="w-full px-3 py-2.5 rounded-xl border border-border text-sm outline-none focus:ring-2 focus:ring-foreground/10 resize-none" />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-muted-foreground mb-1 block">Texto del botón (CTA)</label>
                      <input data-testid="broadcast-cta" value={broadcastCta} onChange={e => setBroadcastCta(e.target.value)} className="w-full h-10 px-3 rounded-xl border border-border text-sm outline-none focus:ring-2 focus:ring-foreground/10" />
                    </div>

                    {/* Recipients */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-xs font-medium text-muted-foreground">Destinatarios ({broadcastSelected.length}/{broadcastRecipients.length})</label>
                        <button
                          onClick={() => setBroadcastSelected(broadcastSelected.length === broadcastRecipients.length ? [] : broadcastRecipients.map(u => u.user_id))}
                          className="text-[10px] text-muted-foreground hover:text-foreground transition-colors"
                        >
                          {broadcastSelected.length === broadcastRecipients.length ? 'Deseleccionar todos' : 'Seleccionar todos'}
                        </button>
                      </div>
                      {broadcastLoading ? (
                        <div className="flex items-center justify-center py-4"><Loader2 className="size-4 animate-spin text-muted-foreground" /></div>
                      ) : broadcastRecipients.length === 0 ? (
                        <p className="text-xs text-muted-foreground/50 py-3 text-center">Todos los usuarios ya tienen este libro</p>
                      ) : (
                        <div className="max-h-40 overflow-y-auto border border-border rounded-xl divide-y divide-border">
                          {broadcastRecipients.map(u => (
                            <label key={u.user_id} className="flex items-center gap-3 px-3 py-2 hover:bg-secondary/30 cursor-pointer transition-colors">
                              <input
                                type="checkbox"
                                checked={broadcastSelected.includes(u.user_id)}
                                onChange={() => setBroadcastSelected(prev => prev.includes(u.user_id) ? prev.filter(id => id !== u.user_id) : [...prev, u.user_id])}
                                className="rounded accent-foreground"
                              />
                              <div className="min-w-0 flex-1">
                                <p className="text-xs font-medium truncate">{u.name || 'Sin nombre'}</p>
                                <p className="text-[10px] text-muted-foreground truncate">{u.email}</p>
                              </div>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Right: Live preview */}
                  <div className="flex-1 p-6 bg-secondary/20">
                    <p className="text-xs font-medium text-muted-foreground mb-3">Vista previa del correo</p>
                    <div className="bg-white rounded-xl shadow-sm border border-border overflow-hidden">
                      <div className="p-6 text-center" style={{ fontFamily: "-apple-system, sans-serif" }}>
                        <img
                          src={`${R2_URL}/books/${broadcastBook.imageFolder}/cover.webp`}
                          alt={broadcastBook.title}
                          className="w-32 h-auto rounded-xl shadow-md mx-auto mb-5"
                        />
                        <h2 className="text-lg font-extrabold text-[#111] mb-2 leading-tight">
                          {broadcastHeadline.replace('{nombre}', 'María')}
                        </h2>
                        <p className="text-sm text-[#555] leading-relaxed mb-5 whitespace-pre-line">
                          {broadcastBody.replace('{nombre}', 'María')}
                        </p>
                        <span className="inline-block px-7 py-3 bg-[#111] text-white rounded-xl text-sm font-bold">
                          {broadcastCta}
                        </span>
                        {broadcastBook.price && (
                          <p className="text-sm text-emerald-600 font-semibold mt-3">${broadcastBook.price?.toFixed(2)} USD</p>
                        )}
                        <p className="text-[10px] text-[#bbb] mt-6 pt-4 border-t border-[#eee]">
                          Museo de la Fe — Historias de fe para los más pequeños
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between px-6 py-4 border-t border-border shrink-0 bg-secondary/10">
                <p className="text-xs text-muted-foreground">
                  {broadcastSelected.length > 0
                    ? `Se enviará a ${broadcastSelected.length} usuario${broadcastSelected.length > 1 ? 's' : ''}`
                    : 'Selecciona al menos un destinatario'}
                </p>
                <div className="flex gap-2">
                  <button onClick={() => setBroadcastBook(null)} className="h-10 px-5 rounded-xl border border-border text-sm font-medium hover:bg-secondary transition-colors">
                    Cancelar
                  </button>
                  <button
                    data-testid="broadcast-send-btn"
                    onClick={sendBroadcast}
                    disabled={broadcastSending || broadcastSelected.length === 0 || !broadcastSubject || !broadcastHeadline}
                    className="h-10 px-6 rounded-xl bg-foreground text-background text-sm font-semibold hover:bg-foreground/90 transition-colors disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {broadcastSending ? <><Loader2 className="size-4 animate-spin" /> Enviando...</> : <><Send className="size-4" /> Enviar</>}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminPage;
